import { ToggleButton, Typography } from '@compass/components'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { ContentColumn, SubHeader } from 'features/commonUI'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { ampli } from 'models/ampli'
import { IdEventV3, IdEventV4 } from 'models/event'
import { useEffect, useRef, useState } from 'react'

import { DeviceContent } from './DeviceContent'
import { EventLayout } from './EventLayout'
import { FraudScoreCard } from './FraudScoreCard'
import { SmartSignalDetection } from './SmartSignalDetection'
import { SmartSignalHighlights } from './SmartSignalHighlights'

export interface VisitDetailsProps {
  requestId: string
  event?: IdEventV4
  eventJson?: IdEventV3 | IdEventV4
  error?: Error | null
}

export function VisitDetails({ event, eventJson, requestId, error }: VisitDetailsProps) {
  const [showDetails, setShowDetails] = useState(false)

  const hash = useLocation({ select: (l) => l.hash })
  const navigate = useNavigate()
  const smartSignalsRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (['scroll-smart-signals', 'smart-signals'].includes(hash) && smartSignalsRef.current != null) {
      smartSignalsRef.current?.scrollIntoView({ behavior: 'smooth' })

      if (hash === 'scroll-smart-signals') {
        setTimeout(() => navigate({ hash: 'smart-signals' }), 150)
      }
    }
  }, [hash, navigate])

  return (
    <EventLayout requestId={requestId} error={error} event={event} eventJson={eventJson}>
      <ContentColumn>
        <SubHeader
          keepRowLayout
          title={
            <Typography size='lg' weight='medium' as='h3'>
              Device
            </Typography>
          }
          actions={
            <ToggleButton
              isSelected={showDetails}
              isDisabled={event == null}
              variant='secondary'
              size='sm'
              isIcon={false}
              onPress={() =>
                setShowDetails((d) => {
                  const newValue = !d
                  if (newValue) {
                    ampli.identificationEventDetailsDeviceDetailsViewed()
                  }
                  return newValue
                })
              }
            >
              {({ isSelected }) => (
                <>
                  {isSelected ? <EyeOffIcon /> : <EyeIcon />}
                  {isSelected ? 'Hide details' : 'Show details'}
                </>
              )}
            </ToggleButton>
          }
        />
        <DeviceContent event={event} showDetails={showDetails} />
      </ContentColumn>

      <ContentColumn>
        <SubHeader
          title={
            <Typography size='lg' weight='medium' as='h3'>
              Smart Signals
            </Typography>
          }
        />
        <SmartSignalHighlights event={event} />
        <FraudScoreCard event={event} />
        <SmartSignalDetection event={event} ref={smartSignalsRef} />
      </ContentColumn>
    </EventLayout>
  )
}
